import pluralize from 'pluralize';
import { CHANGE_SYNC_CONTENT } from './content.js';
import { getOrgSlug } from '../lib/utils.js';

// Function to generate placeholders and their replacements
const generatePlaceholders = (naming) => {
  const tempNaming = {};
  const placeholders = {};
  const pluralPlaceholders = {};

  Object.keys(naming).forEach((key) => {
    const { default: defaultWord, custom: customWord } = naming[key];
    const placeholder = `__${key}__`;
    const pluralPlaceholder = `__${key}_plural__`;

    // Determine effective words for replacement
    const effectiveWord = customWord || defaultWord;
    const effectivePluralWord = customWord
      ? pluralize(effectiveWord)
      : capitalize(pluralize(defaultWord));
    const defaultPluralWord = capitalize(pluralize(defaultWord));

    // Map default words and plural forms to placeholders
    tempNaming[defaultWord] = placeholder;
    tempNaming[defaultPluralWord] = pluralPlaceholder;
    placeholders[placeholder] = effectiveWord;
    pluralPlaceholders[pluralPlaceholder] = effectivePluralWord;
  });

  return { tempNaming, placeholders, pluralPlaceholders };
};

// Function to capitalize a string
const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

// Function to replace default words with placeholders
const replaceWithPlaceholders = (message, tempNaming) => {
  let modifiedMessage = message;

  // Sort keys by length (longest first) to prevent shorter terms from breaking longer matches
  const sortedKeys = Object.keys(tempNaming).sort(
    (a, b) => b.length - a.length
  );

  sortedKeys.forEach((defaultWord) => {
    const placeholder = tempNaming[defaultWord];
    const defaultWordRegex = new RegExp(`\\b${defaultWord}\\b`, 'gi');
    modifiedMessage = modifiedMessage.replace(defaultWordRegex, placeholder);
  });

  return modifiedMessage;
};

// Function to replace placeholders with custom values
const replacePlaceholdersWithCustomValues = (
  message,
  placeholders,
  pluralPlaceholders
) => {
  let modifiedMessage = message;

  // Sort by length (longest first) and replace plural placeholders with custom values first
  const sortedPluralKeys = Object.keys(pluralPlaceholders).sort(
    (a, b) => b.length - a.length
  );

  sortedPluralKeys.forEach((pluralPlaceholder) => {
    const pluralPlaceholderRegex = new RegExp(
      `\\b${pluralPlaceholder}\\b`,
      'gi'
    );
    modifiedMessage = modifiedMessage.replace(
      pluralPlaceholderRegex,
      (match) => {
        return pluralPlaceholders[pluralPlaceholder];
      }
    );
  });

  // Sort by length (longest first) and replace placeholders with custom values
  const sortedKeys = Object.keys(placeholders).sort(
    (a, b) => b.length - a.length
  );

  sortedKeys.forEach((placeholder) => {
    const customWord = placeholders[placeholder];
    const placeholderRegex = new RegExp(`\\b${placeholder}\\b`, 'gi');

    modifiedMessage = modifiedMessage.replace(placeholderRegex, (match) => {
      // Preserve capitalization for custom values
      if (match[0] === match[0].toUpperCase()) {
        return customWord.charAt(0).toUpperCase() + customWord.slice(1);
      }
      return customWord;
    });
  });

  return modifiedMessage;
};

// Main function to replace words in a message based on provided naming conventions
const replaceWords = (message, naming) => {
  const { tempNaming, placeholders, pluralPlaceholders } =
    generatePlaceholders(naming);

  // Step 1: Replace default words with placeholders
  let modifiedMessage = replaceWithPlaceholders(message, tempNaming);

  // Step 2: Replace placeholders with custom values
  modifiedMessage = replacePlaceholdersWithCustomValues(
    modifiedMessage,
    placeholders,
    pluralPlaceholders
  );

  return modifiedMessage;
};

// Function to get the organization terms content
export const getOrgTermsContent = (path, caseFormat = 'none') => {
  let terminology;

  try {
    const customNamingString = localStorage.getItem(
      `${getOrgSlug()}_CUSTOM_NAMING_SETTING`
    );
    terminology = customNamingString ? JSON.parse(customNamingString) : null;
  } catch (error) {
    terminology = null;
  }

  const customNaming = {
    parentPortfolio: { default: 'parent portfolio', custom: '' },
    portfolio: { default: 'portfolio', custom: '' },
    project: { default: 'project', custom: '' },
    initiative: { default: 'initiative', custom: '' },
    activity: { default: 'activity', custom: '' },
  };

  terminology = terminology || customNaming;

  // Extract content value based on path
  const getValueByPath = (obj, path) =>
    path && path.split('.').reduce((acc, part) => acc && acc[part], obj);

  let contentValue = getValueByPath(CHANGE_SYNC_CONTENT, path);
  if (!contentValue) {
    contentValue = path || '';
  }

  // Replace words in the content value
  contentValue = replaceWords(contentValue, terminology);

  return contentValue;
};
